import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { 
  createTask, 
  analyzeText,
  getAgents, 
  getFunctionalAreas, 
  getPriorities,
  uploadFile 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Loader2, 
  Sparkles,
  X,
  Link as LinkIcon,
  Plus,
  Save,
  Image
} from 'lucide-react';
import { toast } from 'sonner';
import DropZone from '@/components/DropZone';
import RichTextEditor from '@/components/RichTextEditor';

const NewTaskPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const analysisData = location.state?.analysisData;
  
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Config
  const [areas, setAreas] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Form
  const [form, setForm] = useState({
    title: analysisData?.title || '',
    description: analysisData?.description || '',
    functional_area: analysisData?.functional_area || 'Otros',
    priority: analysisData?.priority || 'Media',
    internal_agents: [],
    external_agents: [],
    attachments: [],
    urls: [],
    next_step: '',
  });
  
  const [newUrl, setNewUrl] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const [areasRes, prioritiesRes, agentsRes] = await Promise.all([
        getFunctionalAreas(),
        getPriorities(),
        getAgents(),
      ]);
      setAreas(areasRes.data.areas);
      setPriorities(prioritiesRes.data.priorities);
      setAgents(agentsRes.data);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!form.description.trim()) {
      toast.error('Escribe una descripcion para analizar');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await analyzeText(form.description);
      const analysis = response.data;
      
      setForm(prev => ({
        ...prev,
        title: analysis.title || prev.title,
        functional_area: analysis.functional_area || prev.functional_area,
        priority: analysis.priority || prev.priority,
      }));
      
      toast.success('Analisis completado');
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Error al analizar el texto');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileUpload = async (newFiles) => {
    setForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newFiles]
    }));
  };

  const handleAddUrl = () => {
    if (!newUrl.trim()) return;
    
    try {
      new URL(newUrl);
      setForm(prev => ({
        ...prev,
        urls: [...prev.urls, newUrl.trim()]
      }));
      setNewUrl('');
    } catch {
      toast.error('URL no valida');
    }
  };

  const handleRemoveAttachment = (index) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveUrl = (index) => {
    setForm(prev => ({
      ...prev,
      urls: prev.urls.filter((_, i) => i !== index)
    }));
  };

  const toggleAgent = (agentId, type) => {
    const field = type === 'internal' ? 'internal_agents' : 'external_agents';
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(agentId)
        ? prev[field].filter(id => id !== agentId)
        : [...prev[field], agentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('El titulo es requerido');
      return;
    }

    setLoading(true);
    try {
      const response = await createTask(form);
      toast.success('Tarea creada correctamente');
      navigate(`/tasks/${response.data.id}`);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la tarea');
    } finally {
      setLoading(false);
    }
  };

  const internalAgents = agents.filter(a => a.agent_type === 'internal');
  const externalAgents = agents.filter(a => a.agent_type === 'external');

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="new-task-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/tasks')}
            className="text-slate-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
              Nueva Tarea
            </h1>
            <p className="text-slate-500">Completa los detalles de la tarea</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Informacion Basica
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titulo *</Label>
                    <Input
                      id="title"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Titulo de la tarea"
                      className="border-stone-200"
                      data-testid="task-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="description">Descripcion</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleAnalyze}
                        disabled={analyzing || !form.description.trim()}
                        className="text-emerald-700 hover:text-emerald-800"
                        data-testid="analyze-btn"
                      >
                        {analyzing ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-1" />
                        )}
                        Analizar con IA
                      </Button>
                    </div>
                    <RichTextEditor
                      value={form.description}
                      onChange={(val) => setForm({ ...form, description: val })}
                      placeholder="Describe la tarea en detalle..."
                      rows={5}
                      data-testid="task-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Area Funcional</Label>
                      <Select 
                        value={form.functional_area} 
                        onValueChange={(v) => setForm({ ...form, functional_area: v })}
                      >
                        <SelectTrigger className="border-stone-200" data-testid="task-area-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {areas.map((area) => (
                            <SelectItem key={area} value={area}>{area}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Prioridad</Label>
                      <Select 
                        value={form.priority} 
                        onValueChange={(v) => setForm({ ...form, priority: v })}
                      >
                        <SelectTrigger className="border-stone-200" data-testid="task-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {priorities.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="next_step">Proximo Paso (opcional)</Label>
                    <Textarea
                      id="next_step"
                      value={form.next_step}
                      onChange={(e) => setForm({ ...form, next_step: e.target.value })}
                      placeholder="Describe el siguiente paso a realizar..."
                      rows={2}
                      className="border-stone-200"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Attachments */}
              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Archivos y Enlaces
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* File Upload - DropZone */}
                  <div className="space-y-2">
                    <Label>Archivos Adjuntos</Label>
                    <DropZone
                      onFilesUploaded={handleFileUpload}
                      uploading={uploading}
                      setUploading={setUploading}
                    />
                    {form.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.attachments.map((att, index) => {
                          const filename = typeof att === 'string' ? `Archivo ${index + 1}` : att.filename;
                          return (
                            <div 
                              key={index}
                              className="flex items-center gap-2 bg-stone-100 px-3 py-1 rounded-full text-sm"
                            >
                              <Image className="w-4 h-4 text-slate-500" />
                              <span className="truncate max-w-[200px]">{filename}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(index)}
                                className="text-slate-500 hover:text-red-500"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* URLs */}
                  <div className="space-y-2">
                    <Label>Enlaces</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        placeholder="https://ejemplo.com"
                        className="border-stone-200"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddUrl}
                        className="border-stone-200"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {form.urls.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {form.urls.map((url, index) => (
                          <div 
                            key={index}
                            className="flex items-center gap-2 bg-stone-100 px-3 py-2 rounded-lg text-sm"
                          >
                            <LinkIcon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <span className="truncate flex-1">{url}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveUrl(index)}
                              className="text-slate-500 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Agents */}
            <div className="space-y-6">
              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Agentes Internos
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 max-h-64 overflow-y-auto">
                  {internalAgents.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay agentes internos</p>
                  ) : (
                    <div className="space-y-2">
                      {internalAgents.map((agent) => (
                        <div 
                          key={agent.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`internal-${agent.id}`}
                            checked={form.internal_agents.includes(agent.id)}
                            onCheckedChange={() => toggleAgent(agent.id, 'internal')}
                          />
                          <label 
                            htmlFor={`internal-${agent.id}`}
                            className="text-sm text-slate-700 cursor-pointer"
                          >
                            {agent.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Agentes Externos
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 max-h-64 overflow-y-auto">
                  {externalAgents.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay agentes externos</p>
                  ) : (
                    <div className="space-y-2">
                      {externalAgents.map((agent) => (
                        <div 
                          key={agent.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`external-${agent.id}`}
                            checked={form.external_agents.includes(agent.id)}
                            onCheckedChange={() => toggleAgent(agent.id, 'external')}
                          />
                          <label 
                            htmlFor={`external-${agent.id}`}
                            className="text-sm text-slate-700 cursor-pointer"
                          >
                            {agent.name}
                            {agent.company && (
                              <span className="text-slate-400 text-xs ml-1">({agent.company})</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12"
                data-testid="create-task-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Crear Tarea
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewTaskPage;
